import Logo from 'assett/icons/logo.svg';
import { setDialogOpen } from 'feature/hooks/dialogSlice';
import useDevice from 'hooks/DesktopDevice.hook';
import useDialogConfirm from 'hooks/DialogConfirm.hook';
import { useResetPage } from 'hooks/utils.hook';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { RootState } from 'store/store';
import { Box } from 'styles';
import CopyButton from 'styles/button/CopyButton';
import CardMedia from 'styles/card/CardMedia';
import Container from 'styles/container/Container';
import Stack from 'styles/stack/Stack';
import Typography from 'styles/typography/Typograpy';
import { VERSION } from 'types/dialogTypes';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';
import { FooterDesktop } from '../FooterDesktop/FooterDesktop';
import Sidebar from '../Sidebar/Sidebar.component';
import Breadcrumbs from './Breadcrumbs.component';

export default function SharedLayout() {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const dispatch = useDispatch();
	const location = useLocation();

	const { isDesktop } = useDevice();
	const confirmDialog = useDialogConfirm();

	const { uuid, id, complaintId, fraudId } = useParams();
	const { claimData } = useSelector((store: RootState) => store.claim);

	const paths = location.pathname.split('/').map(e => {
		if (decodeURI(e) === uuid) {
			// even with the uuid in the path, we want to show the original page title
			if (location.pathname.includes('view-settlement-letters')) {
				return tClientLabels(`view-settlement-letters.pageTitle`);
			}

			if (!!uuid && !location.pathname.includes('check-and-submit')) {
				return `${tClientLabels('claim.breadcrumb')} #${claimData.externalClaimId}`;
			}

			return uuid;
		}

		if (decodeURI(e) === complaintId) {
			if (
				location.pathname.includes('view-complaint-tracking') ||
				location.pathname.includes('view-complaint')
			) {
				return `${tClientLabels('complaint.breadcrumb')} #${complaintId}`;
			}
		}

		if (decodeURI(e) === fraudId) {
			if (
				location.pathname.includes('view-fraud-tracking') ||
				location.pathname.includes('view-fraud')
			) {
				return `${tClientLabels('fraud.breadcrumb')} #${fraudId}`;
			}
		}

		return tClientLabels(`${e}.pageTitle`);
	});

	useResetPage();

	return (
		<Container sx={{ height: '100vh', overflow: 'hidden' }}>
			<Sidebar />
			<Stack width={'100%'}>
				<Stack direction={'row'} justifyContent={'space-between'}>
					<Stack sx={{ mt: { sm: 6.25, lg: 8.75 } }}>
						<Stack direction={'row'} alignItems={'center'} gap={'20px'}>
							<Typography variant="title2">
								{location.pathname
									.split('/')
									.map(
										(s: string, index: number) =>
											index === paths.length - 1 && paths[index],
									)}
							</Typography>
							{!!uuid && location.pathname.includes('view-claims') && (
								<CopyButton text={claimData.externalClaimId} />
							)}
						</Stack>

						<Breadcrumbs
							claimUuid={claimData.claimUUID || ''}
							externalClaimId={claimData.externalClaimId}
							id={id || ''}
						/>
					</Stack>
					<Stack alignItems={'flex-end'} sx={{ mt: { sm: 6.25, lg: 8.75 } }}>
						<Box width="48px" height="60px" sx={{ cursor: 'pointer' }}>
							<CardMedia
								component="img"
								image={Logo}
								onClick={() => {
									confirmDialog.open(VERSION);
									dispatch(setDialogOpen(true));
								}}
							/>
						</Box>
					</Stack>
				</Stack>
				<Stack
					sx={{
						height: `calc(100vh - ${isDesktop ? '228px' : '198px'})`,
						maxWidth: `calc(100vw - ${isDesktop ? '260px' : '180px'})`,
					}}
				>
					<Outlet />
				</Stack>
				<Stack direction="row">
					<FooterDesktop />
				</Stack>
			</Stack>
		</Container>
	);
}
